import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";
import { DealFeeEntry, ListEnvelope } from "src/dm/interfaces/DealActivity";

export interface FeeWriteRequest {
  feeType: string;
  supportTeamMember?: string | null;
  apVendorNumber?: string | null;
  businessUnit?: string | null;
  addressSequence?: number | null;
  entryType?: string | null;
  invoiceDescription?: string | null;
  feeAmount?: number | null;
}

const feesUrl = (dealId: number) => `${APIS.Deals}/${dealId}/fees`;

export const useFeeList = (dealId: number | undefined) =>
  useQuery({
    queryKey: ["deal-fees", dealId],
    enabled: dealId !== undefined && dealId > 0,
    queryFn: async (): Promise<DealFeeEntry[]> => {
      const response = await apiClient.get<ListEnvelope<DealFeeEntry>>(
        feesUrl(dealId as number)
      );
      return response.data.items;
    },
  });

export const useAddFee = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: FeeWriteRequest): Promise<DealFeeEntry> => {
      const response = await apiClient.post<DealFeeEntry>(feesUrl(dealId), body);
      return response.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-fees", dealId] }),
  });
};

export const useUpdateFee = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      feeId,
      feeType,
      body,
    }: {
      feeId: number;
      feeType: string;
      body: FeeWriteRequest;
    }): Promise<DealFeeEntry> => {
      const response = await apiClient.put<DealFeeEntry>(
        `${feesUrl(dealId)}/${feeId}/${encodeURIComponent(feeType)}`,
        body
      );
      return response.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-fees", dealId] }),
  });
};

export const useDeleteFee = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ feeId, feeType }: { feeId: number; feeType: string }): Promise<void> => {
      await apiClient.delete(`${feesUrl(dealId)}/${feeId}/${encodeURIComponent(feeType)}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["deal-fees", dealId] }),
  });
};
